import * as React from "react";
import { useCallback, useState } from "react";
import { shipficiencyLogo } from "@src/branding/shipficiencyLogo";
import { formatCurrency } from "@taskpane/utils/shipmentUtils";
import { strings } from "@src/i18n/strings";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  background: "linear-gradient(180deg, #f0f4f8 0%, #e8edf2 100%)",
  padding: "2rem 1.5rem",
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  maxWidth: "400px",
  width: "90%",
  maxHeight: "85vh",
  overflow: "auto",
  fontFamily:
    '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
};
const imageContainerStyle: React.CSSProperties = {
  width: "140px",
  height: "140px",
  marginBottom: "1rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const imageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  borderRadius: "12px",
  filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.08))",
};
const headingStyle: React.CSSProperties = {
  fontSize: "1.15rem",
  fontWeight: 600,
  color: "#1a2b3c",
  margin: "0 0 0.5rem 0",
  lineHeight: 1.3,
  textAlign: "center",
};
const subTextStyle: React.CSSProperties = {
  fontSize: "0.85rem",
  color: "#5a6a7a",
  margin: "0 0 1rem 0",
  lineHeight: 1.5,
  textAlign: "center",
};
const offerListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  width: "100%",
  marginBottom: "1.25rem",
};
const offerItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  padding: "0.75rem 1rem",
  background: "#ffffff",
  border: "2px solid #e1e5e9",
  borderRadius: "10px",
  cursor: "pointer",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
};
const offerItemSelectedStyle: React.CSSProperties = {
  borderColor: "#037bff",
  boxShadow: "0 2px 8px rgba(3, 123, 255, 0.2)",
};
const radioOuterStyle: React.CSSProperties = {
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  border: "2px solid #037bff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
const radioInnerStyle: React.CSSProperties = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "#037bff",
};
const offerContentStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};
const offerCarrierStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.95rem",
  color: "#1a2b3c",
};
const offerPriceStyle: React.CSSProperties = {
  fontSize: "0.85rem",
  color: "#037bff",
  fontWeight: 600,
  marginTop: "2px",
};
const buttonRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.75rem",
  width: "100%",
  justifyContent: "flex-end",
};
const buttonBaseStyle: React.CSSProperties = {
  padding: "0.65rem 1.5rem",
  fontSize: "0.9rem",
  fontWeight: 600,
  border: "none",
  borderRadius: "24px",
  cursor: "pointer",
  transition: "background-color 0.2s ease, transform 0.1s ease",
};
const cancelButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  color: "#5a6a7a",
  backgroundColor: "#e8edf2",
};
const submitButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  color: "#ffffff",
  backgroundColor: "#037bff",
  boxShadow: "0 2px 8px rgba(3, 123, 255, 0.3)",
};

export interface OfferSelectionModalProps {
  open: boolean;
  offers: Array<{
    id: string;
    name: string;
    price: number;
  }>;
  onCancel: () => void;
  onSubmit: Function;
}
export default function OfferSelectionModal({
  open,
  offers,
  onCancel,
  onSubmit,
}: OfferSelectionModalProps) {
  const modalStrings = strings.components.offerSelectionModal;
  const cheapestId = (
    list: Array<{
      id: string;
      price: number;
    }>
  ) => (list.length > 0 ? [...list].sort((a, b) => a.price - b.price)[0].id : null);
  const [selectedId, setSelectedId] = useState<string | null>(() => cheapestId(offers));

  React.useEffect(() => {
    if (open && offers.length > 0) {
      setSelectedId(cheapestId(offers));
    }
  }, [open, offers]);
  const handleSubmit = useCallback(() => {
    if (selectedId) {
      onSubmit(selectedId);
    }
  }, [selectedId, onSubmit]);
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onCancel();
      }
    },
    [onCancel]
  );

  if (!open) return null;
  return (
    <div
      data-testid="offer-selection-overlay"
      style={overlayStyle}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="offer-modal-heading"
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={imageContainerStyle}>
          <img src={shipficiencyLogo} alt={modalStrings.imageAlt} style={imageStyle} />
        </div>

        <h2 id="offer-modal-heading" style={headingStyle}>
          {modalStrings.title}
        </h2>

        <p style={subTextStyle}>{modalStrings.body}</p>

        <div style={offerListStyle}>
          {[...offers]
            .sort((a, b) => a.price - b.price)
            .map(({ id, name, price }) => {
              const isSelected = selectedId === id;
              const carrier = name || modalStrings.unknownCarrier;

              return (
                <button
                  key={id}
                  type="button"
                  style={{
                    ...offerItemStyle,
                    ...(isSelected ? offerItemSelectedStyle : {}),
                  }}
                  onClick={() => setSelectedId(id)}
                >
                  <div style={radioOuterStyle}>{isSelected && <div style={radioInnerStyle} />}</div>
                  <div style={offerContentStyle}>
                    <div style={offerCarrierStyle}>{carrier}</div>
                    <div style={offerPriceStyle}>{formatCurrency(price)}</div>
                  </div>
                </button>
              );
            })}
        </div>

        <div style={buttonRowStyle}>
          <button type="button" style={cancelButtonStyle} onClick={onCancel}>
            {modalStrings.cancel}
          </button>
          <button
            type="button"
            style={submitButtonStyle}
            onClick={handleSubmit}
            disabled={!selectedId}
          >
            {modalStrings.submit}
          </button>
        </div>
      </div>
    </div>
  );
}
